import type {QueryClient} from "@tanstack/react-query";
import type {NavigateFunction} from "react-router-dom";

import {briefingBoardsService} from "@/src/modules/briefing-boards/services";

import {NOTIFICATION_FEED_QUERY_KEY} from "../hooks/use-notification-feed";
import {dispatchShiftComplianceGateFromNotification} from "./dispatch-shift-compliance-gate-from-notification";
import {ackDirectMessageNotificationItem} from "./direct-message-notification-read";
import {markNotificationItemRead} from "./mark-notification-item-read";
import {isShiftNotificationTopic} from "./notification-feed-topics";
import type {NotificationItem} from "../types";

function getBriefingBoardId(item: NotificationItem): number | null {
  if (typeof item.payload.briefing_board_id === "number") {
    return item.payload.briefing_board_id;
  }
  if (item.topic === "briefing_board" && typeof item.subject.id === "number") {
    return item.subject.id;
  }
  return null;
}

function getBriefingShiftId(item: NotificationItem): number | null {
  return typeof item.payload.shift_id === "number" ? item.payload.shift_id : null;
}

/** Shift compliance: open blocking gate in place. DM/BB: navigate after domain ack where required. */
export async function openNotificationSubject(
  item: NotificationItem,
  path: string,
  navigate: NavigateFunction,
  queryClient?: QueryClient,
): Promise<void> {
  if (isShiftNotificationTopic(item.topic)) {
    dispatchShiftComplianceGateFromNotification(item);
    return;
  }

  if (item.topic === "briefing_board") {
    const briefingId = getBriefingBoardId(item);
    const shiftId = getBriefingShiftId(item);

    if (briefingId && shiftId) {
      try {
        await briefingBoardsService.markBriefingBoardRead(briefingId, shiftId);
        if (queryClient) {
          await queryClient.invalidateQueries({queryKey: [NOTIFICATION_FEED_QUERY_KEY]});
        }
      } catch {
        // Still navigate so the user can read from the briefing UI.
      }
    }

    navigate(path);
    return;
  }

  if (item.topic === "direct_message") {
    await ackDirectMessageNotificationItem(item, queryClient);
    navigate(path);
    return;
  }

  if (item.topic === "stored_vehicle") {
    await markNotificationItemRead(item, queryClient);
    navigate(path);
    return;
  }

  navigate(path);
}
